import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {MvsObjectService} from 'features/core/shared/service/object/mvs-object.service';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {ObjectRequestList} from 'features/core/shared/dto/object/request/object-request-list';
import {JbSpringBatchIndicatorService} from 'features/feature-tech/jb/service/jb-spring-batch-indicator.service';
import {CpAgentTransactionService} from '../../../../../service/api/cp-agent-transaction.service';
import {
    ProvisionAccountStatementRequestDto,
    ProvisionAccountStatementResponseDto,
    ProvisionAgentTransactionWithBalanceDto,
    ProvisionContractTransactionDetailDto,
    ProvisionStatementSortDirection
} from '../../../../../model/dto/api/provision-account-statement.dto';
import {ProvisionAgentBalanceDto} from '../../../../../model/dto/entity/provision-agent-balance.dto';
import {ProvisionAccountKind} from '../../../../../enum/provision-account-kind.enum';
import {ProvisionBookingKind} from '../../../../../enum/provision-booking-kind.enum';
import {ProvisionTransactionEvent} from '../../../../../enum/provision-transaction-event.enum';
import {ProvisionTransactionStatus} from '../../../../../enum/provision-transaction-status.enum';
import {
    getProvisionAccountKindLabel,
    PROVISION_ACCOUNT_KIND_OPTIONS,
    ProvisionAccountKindOption
} from '../../../../../enum/provision-account-kind-label';
import {getProvisionBookingKindLabel} from '../../../../../enum/provision-booking-kind-label';
import {
    CpTransactionStatusSeverity,
    getProvisionTransactionStatusLabel,
    getProvisionTransactionStatusSeverity
} from '../../../../../enum/provision-transaction-status-label';
import {getProvisionTransactionEventLabel} from '../../../../../enum/provision-transaction-event-label';
import {CpFeatureRight} from '../../../../../service/domain/cp-feature-right';

/**
 * navItem "Konten &amp; Salden" (action `account`) of the ProvisionAgent OBC — the account-driven
 * statement of ONE agent on ONE of its two accounts. Replaces the two former navItems
 * "Konten/Salden" and "Transaktionen" (decision D1).
 *
 * <h2>THE ONE RULE OF THIS COMPONENT — the sign is never re-derived here (design gate FD-1, B1)</h2>
 * The backend delivers {@link ProvisionAgentTransactionWithBalanceDto#signedAmount} and
 * {@link ProvisionAgentTransactionWithBalanceDto#runningBalance} ALREADY SIGNED, folded over the
 * module's single sign definition (`ProvisionBalanceService.signedAmount`). This component colours,
 * compares and renders by the SIGN OF THOSE DELIVERED NUMBERS and by nothing else. `bookingKind`
 * is used EXCLUSIVELY as the German caption of the "Richtung" column ({@link bookingKindLabel}) —
 * never negated, never turned into a colour, never turned back into a direction for arithmetic.
 * The bm idiom `transactionDirection === CREDIT`
 * (bm-customer-account-transaction.component.html:146, :201) is deliberately NOT copied over,
 * although this component copies that file's SHAPE. cp already carries one TypeScript copy of the
 * sign rule (`cp-provision-agent-manual-booking-dialog.component.ts:328`); that copy is signed-off
 * legacy debt and a second one would be a defect.
 *
 * <h2>Enums travel as ordinals — never truthiness-test them</h2>
 * Every cp enum mirrors a backend `AlphaBaseEnum` whose `@JsonValue` writes the ORDINAL, so the
 * TypeScript values are numbers. `PROVISION_ACCOUNT`, `CALCULATED`, `CREDIT` and
 * `PROVISION_BOOKING` are all `0` and therefore FALSY. Every check below compares explicitly
 * (`=== ProvisionTransactionStatus.CANCELLED`) or probes for absence (`!= null`); an `if (row.status)`
 * would silently drop every CALCULATED row.
 *
 * <h2>Every request carries a ticket — the last one asked wins, not the first one answered</h2>
 * Account switch, checkboxes, sort toggle, period and "Weitere Buchungen laden" all fire a fresh
 * statement request without cancelling the previous one. Responses may overtake each other, so each
 * request takes the next {@link statementSequence} ticket and a response with a stale ticket is
 * dropped whole — its data, its error and its `busy` reset. Reserve lines under the heading
 * "Provisionskonto — realer Saldo" would otherwise be one late HTTP answer away, on the screen that
 * decides payouts. {@link ProvisionAccountStatementResponseDto#accountKind} is checked on top as a
 * second net.
 *
 * <h2>What is unknown is shown as unknown</h2>
 * The head figures are `null` — rendered "—" — while nothing has been loaded or the last load
 * failed; they are never zeroed. A green 0,00 € beside a red error message states "settled account,
 * payout harmless". For the same reason a failed read of the balance row raises
 * {@link balanceLoadFailed} and says so, instead of rendering an unknown reserve like a covered one.
 * `openingBalance` follows the same rule: an empty page has no balance to state before its first
 * line, so it arrives `null` and is printed "—" — never "0,00 €".
 *
 * <h2>Head figures are READ, not re-aggregated (decision D4, GR-5)</h2>
 * `currentBalance` / `simulatedBalance` of the response come from the persisted
 * `cp.ProvisionAgentBalance` row — the same numbers the payout brake uses. They are displayed as
 * delivered and never recomputed from the visible lines; the balance column follows
 * {@link ProvisionAccountStatementResponseDto#runningBalanceSimulated}, which is what labels it.
 *
 * <h2>Two reserve statements, and only ONE of them is the payout brake</h2>
 * {@link reserveUnderfunded} IS the brake, and it is READ from
 * {@link ProvisionAccountStatementResponseDto#reserveUnderfunded} — the same source
 * `ProvisionPayoutService` decides on. It is never derived here. The component used to compute
 * `reserveBalance &lt; 0` itself; that was a second copy of the run's rule, and the day the brake
 * gains a tolerance or a coverage ratio the copy would keep firing and assert a brake, on exactly
 * the screen payouts are decided on, that the run does not apply.
 *
 * {@link reserveSimulatedForecastNegative} is the OTHER statement and deliberately not called an
 * underfunding: the SIMULATED reserve balance (ACTIVE + CALCULATED) is negative, i.e. bookings that
 * are calculated but NOT YET ACTIVATED would take the account into the red. The payout run brakes on
 * the REAL balance alone, so this is a forecast — rendered as a calm preview in its own words, never
 * in the red brake banner. The backend ships no flag for it on purpose.
 *
 * <h2>Why the reserve balance row is still loaded separately</h2>
 * The FORECAST needs `reserveSimulatedBalance`, and the statement carries only the balances of the
 * SELECTED account while the reserve notice has to stay visible on the Provisionskonto too
 * (decision D3). The persisted balance values are therefore read once from
 * `cp.ProvisionAgentBalance` through the generic CRUD service — the very data path
 * {@link CpProvisionAgentBalanceComponent} uses, so both screens state the shortfall in the same
 * words. That component survives for the side view (FD-3) and is not duplicated here. A FAILED read
 * of that row costs the forecast and says so ({@link balanceLoadFailed}); it does not touch the
 * brake, which travels with the statement.
 *
 * <h2>Paging is a CURSOR, not a growing page</h2>
 * "Weitere Buchungen laden" fetches the NEXT page over
 * {@link ProvisionAccountStatementResponseDto#nextAnchorTransactionId} and APPENDS it to what is
 * already on screen ({@link loadMore}). It does not grow the requested page size any more.
 *
 * That earlier mechanism re-requested one ever LARGER page and stopped at the module ceiling, so
 * the screen reached 200 of the roughly 10.000 bookings per agent the backend is explicitly
 * designed for ({@code ProvisionAgentAccountStatementService}, "expected volume"), and everything
 * older was reachable only by narrowing the valuta period. {@code ProvisionModuleConfig} says of
 * {@code STATEMENT_PAGE_SIZE_MAX} that it is "not a cap on how far a caseworker can scroll" —
 * with the growing page it was exactly that. Over the cursor it is again what it says: the size of
 * ONE page.
 *
 * The anchor is EXCLUSIVE — it names the first line BEYOND the page just delivered — and the
 * backend consumes it INCLUSIVELY as the first line of the page it answers. Sending it back
 * verbatim therefore continues the journal without repeating and without skipping a line. `null` is
 * the module's reliable end-of-journal signal ({@link hasMore}). The inclusive `oldestTransactionId`
 * / `newestTransactionId` are RE-READ anchors and would duplicate the boundary line; they are not
 * used for paging.
 *
 * There is exactly ONE direction. The backend publishes no backwards anchor, so an appended list
 * only ever grows away from where it started; going back means restarting the page.
 *
 * <h2>Appending must not corrupt the balance column — and does not</h2>
 * The backend computes EVERY page in full: it seeds the running balance with
 * {@link ProvisionAccountStatementResponseDto#openingBalance}, the balance before the JOURNAL-OLDEST
 * line OF THAT PAGE, taken from one aggregate over the whole journal and NOT from the page before
 * it. Every `runningBalance` is therefore an ABSOLUTE account balance and stays true wherever its
 * line is appended. The two pages also meet exactly: the `runningBalance` of the first appended line
 * equals the `openingBalance` its predecessor page was seeded with, because that line is the
 * immediate journal-predecessor of the predecessor page's oldest line.
 *
 * The Saldovortrag ROW is the one figure that is NOT per page. It states the balance before the
 * JOURNAL-OLDEST line of EVERYTHING on screen, so it is adopted from the page carrying that line —
 * the newest appended page in a `'desc'` read, the FIRST page in an `'asc'` one; see
 * {@link adoptOpeningBalance}. Adopting it from every page would print the seed of the last chunk
 * under a table that reaches far further back.
 *
 * <h2>Head figures belong to the AGENT, not to the page</h2>
 * `currentBalance`, `simulatedBalance`, `lastCalculatedDate`, `totalTransactionCount` and the payout
 * brake {@link reserveUnderfunded} describe the agent and its filter, not the chunk of lines just
 * fetched, and every response repeats them. They are adopted from the LAST ACCEPTED answer — the
 * freshest read of the same numbers — rather than frozen at the first page, so a balance job that
 * finishes between two clicks is reflected instead of being held stale on the screen that decides
 * payouts.
 *
 * <h2>Page size is the backend's business alone</h2>
 * The component sends {@link pageSize} when a parent bound one and NOTHING otherwise, and keeps no
 * page-size number of its own. The module's silent clamp used to matter because the clamp WAS the
 * reach limit; over the cursor a clamped page merely means one more click.
 *
 * <h2>Status filter: two booleans, not a status list</h2>
 * A running balance is reconcilable only when the accumulated lines are exactly the lines the
 * balance is defined over. `includeCalculated` therefore picks the SCOPE (real = ACTIVE only,
 * simulated = ACTIVE + CALCULATED — the two scopes the four persisted figures use), while
 * `includeCancelled` is the orthogonal visibility flag: a CANCELLED line never moves any balance
 * (GR-3) and repeats the running balance of its predecessor, struck through.
 *
 * <h2>Refresh, two sources (04-frontend.md §4)</h2>
 * `MvsObjectService.objectChangeEvent` for `cp.ProvisionAgentTransaction` catches the bookings the
 * two action dialogs write synchronously; `JbSpringBatchIndicatorService.status$` catches the ones
 * the cp jobs create while they run (calculation, activation, payout) — those report no object
 * change event, so without the second subscription the statement would show, after a run, exactly
 * the rows that existed before it. Reload happens on the running -&gt; not-running transition. The
 * indicator is activated by the shell `CpDashboardPage` (`jobNamePattern: 'cp%'`); outside that
 * shell `status$` simply never fires a transition.
 *
 * ACTIONS: "Manuelle Buchung" and "Umbuchung" reuse the existing dialogs
 * {@link CpProvisionAgentManualBookingDialogComponent} and
 * {@link CpProvisionAgentTransferDialogComponent} UNCHANGED — both build a metadata-driven
 * `MvsFormDto` form widget and own their write. This component contains no form HTML.
 *
 * PERMISSIONS (U-08): {@link canManualBooking} / {@link canReserveTransfer} follow the module-wide
 * optimistic pattern of {@link CpFeatureRight} — granted by default, demoted when the server
 * answers a 403.
 *
 * MODULE BOUNDARY: the bm component `bm-customer-account-transaction.component` is the PATTERN for
 * the head/table/expand shape; no bm code is imported (I11 — no cp -&gt; bm edge).
 */
@Component({
    selector: 'cp-provision-agent-account',
    templateUrl: './cp-provision-agent-account.component.html',
    styleUrls: ['./cp-provision-agent-account.component.scss'],
    standalone: false
})
export class CpProvisionAgentAccountComponent implements OnInit, OnChanges, OnDestroy {

    /**
     * True while a REPLACING statement request is in flight — the state the skeleton renders.
     *
     * An APPEND is deliberately not this flag: the lines already on screen stay valid and readable
     * while the next page is fetched, and blanking the table into a skeleton on every
     * "Weitere Buchungen laden" would lose the reading position the click was made from.
     * See {@link appending}.
     */
    busy: boolean;

    /** True while an APPENDING request ({@link loadMore}) is in flight; drives the button spinner. */
    appending: boolean;

    /** True once the first render decision can be made (skeleton -&gt; content). */
    initialized: boolean;

    @Input() provisionAgentId: number;

    /**
     * OPTIONAL size of ONE page. Left unset — the normal case — the component asks for no size at
     * all and the BACKEND names its default.
     *
     * There is deliberately no literal default here, and no ceiling either: a number typed into the
     * frontend is a second copy of a decision the module already owns. Since paging runs over the
     * cursor {@link nextAnchorTransactionId}, this number decides only how many lines arrive per
     * click — never how far back the caseworker can read. The module clamps a size it considers too
     * large silently, and that is now harmless: a clamped page costs one more click, not reach.
     */
    @Input() pageSize: number;

    /**
     * Optional booking the page starts at (INCLUSIVE, in the display direction). Highlighted in the
     * table so a deep link into one booking stays recognisable.
     *
     * NOT YET BOUND BY ANY PARENT — `cp-provision-agent-full.component.html` passes only
     * `provisionAgentId`, so today the component always opens at the end of the journal.
     *
     * ── FOR WHOEVER WIRES THE DEEP LINK
     * The moment an anchor IS bound, the list reads in ONE direction only: {@link loadMore} follows
     * the forward cursor AWAY from the anchor, along {@link direction}. Bookings on the OTHER side
     * of the anchor — newer than it in a `'desc'` read — are then unreachable AND unannounced,
     * because the module publishes no backwards anchor and this component says nothing about what
     * lies behind the given one. The response's `hasOlder` / `hasNewer` used to be stored here for
     * exactly that purpose and were never read; they are gone rather than left as a promise nothing
     * keeps. Backwards paging is a FEATURE to build with the deep link (a "neuere Buchungen"
     * control plus whatever anchor the module then offers), not a line to re-add here.
     */
    @Input() anchorTransactionId: number;

    /** um right `cp_manualBooking` (U-08) — optimistic default, demoted on 403; see class TSDoc. */
    @Input() canManualBooking: boolean = true;

    /** um right `cp_reserveTransfer` (U-08) — optimistic default, demoted on 403; see class TSDoc. */
    @Input() canReserveTransfer: boolean = true;

    // --- Account selection ------------------------------------------------------------------- //

    /**
     * The account the statement covers. ALWAYS starts on the Provisionskonto (decision D3):
     * a deterministic entry point beats a "smart" one that would open on a different account per
     * agent and make two screens incomparable. A reserve shortfall stays visible regardless,
     * through the head notice.
     */
    accountKind: ProvisionAccountKind = ProvisionAccountKind.PROVISION_ACCOUNT;

    /**
     * The account chooser of the head, from the enum's presentation companion
     * `enum/provision-account-kind-label.ts` — the module convention (cf.
     * `provision-run-status-label.ts`). Names of enum constants belong next to the enum, not into a
     * component: the same two accounts are named in the heading above the table and in the empty
     * state, and one list is the only way they cannot drift apart.
     */
    readonly accountKindOptions: ProvisionAccountKindOption[] = PROVISION_ACCOUNT_KIND_OPTIONS;

    // --- Filter state ------------------------------------------------------------------------ //

    /** Balance scope: true = simulated (ACTIVE + CALCULATED), false = real (ACTIVE only). */
    includeCalculated: boolean = true;

    /** Visibility of CANCELLED lines; they never move a balance either way (GR-3). */
    includeCancelled: boolean = false;

    /** Valuta period, both bounds optional and independent. */
    filterValutaFrom: Date = null;
    filterValutaTo: Date = null;

    /** Display order of the page; `'desc'` (newest first) is the backend default. */
    direction: ProvisionStatementSortDirection = 'desc';

    // --- Response state ----------------------------------------------------------------------- //

    /**
     * EVERY line loaded so far, in display order — the first page plus every page
     * {@link loadMore} appended behind it, never a single page on its own.
     *
     * Appending is sound because each `runningBalance` is an absolute account balance the backend
     * computed for the whole page in one pass; see the class TSDoc. Only a REPLACING load
     * (account switch, filter, sort, refresh) throws the accumulation away.
     */
    transactions: ProvisionAgentTransactionWithBalanceDto[] = [];

    /**
     * Persisted REAL balance of the selected account (D4).
     *
     * `null` means UNKNOWN — never loaded, or the last load failed. It is not `0`: a head figure
     * of "0,00 €" is a STATEMENT ("the account is settled, a payout is harmless"), and printing it
     * while the request failed would be an invented one. The template renders "—" for `null`.
     */
    currentBalance: number = null;

    /** Persisted SIMULATED balance of the selected account (D4); `null` = unknown, see above. */
    simulatedBalance: number = null;

    /** When the persisted balances last changed; null when never calculated. */
    lastCalculatedDate: string = null;

    /**
     * Balance BEFORE the JOURNAL-OLDEST line of everything LOADED — the seed of the running-balance
     * column ({@link ProvisionAgentAccountStatementService} computes it over the oldest key of the
     * page and only THEN reverses the page for a descending display). It therefore belongs ABOVE the
     * first row when {@link direction} is `'asc'` and BELOW the last row when it is `'desc'`; the
     * template renders it in the head or in the foot accordingly.
     *
     * Across appended pages it is NOT taken from every answer but from the page holding the
     * journal-oldest line of the whole list — see {@link adoptOpeningBalance}.
     *
     * `null` = NOT DETERMINED — an empty page has no line to compute a balance before, and a failed
     * load knows nothing at all. Rendered "—", never "0,00 €": a zero here is the statement "the
     * account was settled at that point", which is a fact nobody established.
     */
    openingBalance: number = null;

    /** Which balance the running-balance column follows — it labels the column header. */
    runningBalanceSimulated: boolean = true;

    /**
     * THE PAGING CURSOR: the EXCLUSIVE anchor one step IN the display direction, from
     * {@link ProvisionAccountStatementResponseDto#nextAnchorTransactionId}, and at the same time the
     * end-of-journal signal — `null` means nothing further exists in the direction being read
     * ({@link hasMore}).
     *
     * {@link loadMore} sends it back VERBATIM as `anchorTransactionId`. The backend reads that
     * parameter inclusively as the first line of the page it answers, and this id names the line
     * BEYOND the page just shown, so the two pages abut exactly: no repeated line, no gap.
     *
     * It is advanced ONLY from an accepted response. A response dropped by the request ticket or by
     * the `accountKind` counter-check appends nothing and must not move the cursor — a cursor
     * advanced by a discarded answer would silently skip a page of a money journal.
     *
     * The INCLUSIVE `oldestTransactionId` / `newestTransactionId` are deliberately not kept: they
     * re-read the current page starting AT their own line, so paging on them would repeat the
     * boundary booking as the first line of the next page — a duplicated row in a column of running
     * balances.
     */
    nextAnchorTransactionId: number = null;

    /** How many bookings match the filter in total; `null` = unknown (never loaded / load failed). */
    totalTransactionCount: number = null;

    /** The persisted balance row, read for the simulated reserve forecast (see below). */
    balanceDto: ProvisionAgentBalanceDto = null;

    /**
     * THE PAYOUT BRAKE (U-03), delivered by the statement response — never derived here.
     *
     * `ProvisionPayoutService` decides whether an agent is braked, and
     * {@link ProvisionAccountStatementResponseDto#reserveUnderfunded} is read from that very source.
     * The component used to compute `reserveBalance < 0` itself, which was a COPY of the run's rule:
     * the day the brake gains a tolerance or a coverage ratio, the copy keeps firing and claims a
     * brake — on exactly the screen payouts are decided on — that the run does not apply. There is
     * one authority for this sentence and it is the backend.
     */
    reserveUnderfunded: boolean = false;

    /**
     * FORECAST, NOT THE BRAKE: the SIMULATED reserve balance (ACTIVE + CALCULATED) is negative,
     * i.e. bookings that are calculated but NOT YET ACTIVATED would take the reserve account into
     * the red.
     *
     * The backend deliberately ships no flag for this, and the name deliberately avoids the word
     * "underfunded": the payout run brakes on the REAL reserve balance ALONE. Calling a simulated
     * shortfall an underfunding would repeat, under a new name, exactly the confusion
     * {@link reserveUnderfunded} was cleaned up to end — an operator reading "Unterdeckung" twice in
     * two colours cannot tell which of them stops a payout. It is rendered as a calm preview with
     * its own wording, and the red brake banner never comes from here.
     */
    reserveSimulatedForecastNegative: boolean = false;

    /**
     * THIRD state of the reserve coverage, beside "gedeckt" and "unterdeckt": the balance row could
     * not be read at all.
     *
     * Without it a failed balance call renders exactly like a healthy account — `balanceDto` is
     * null, {@link reserveSimulatedForecastNegative} stays false, and the statement looks
     * unremarkable while the agent may be heading for a five-digit shortfall. "Rücklagenstand
     * unbekannt" must never be shown as "keine Unterdeckung"; the template says so in words instead.
     *
     * It no longer hides the BRAKE: {@link reserveUnderfunded} arrives with the statement and is
     * unaffected by a failed read of this row. What goes missing here is the forecast.
     */
    balanceLoadFailed: boolean = false;

    // --- Error state --------------------------------------------------------------------------- //

    /** True when the last REPLACING statement request failed; the table then shows {@link errorMessage}. */
    loadFailed: boolean = false;

    /**
     * The German sentence of the refusal, taken from the response body. Never replaced by a
     * generic text — that sentence is the only thing the caseworker can act on.
     */
    errorMessage: string = null;

    /**
     * True when the last APPEND failed — a separate state from {@link loadFailed} on purpose.
     *
     * The lines already accumulated were read successfully and their balances are absolute, so a
     * failed "Weitere Buchungen laden" must not swap a full, correct statement for an error page.
     * The failure is reported BESIDE the button, the table stays, and the cursor stays where it was
     * so the same click can simply be repeated.
     */
    appendFailed: boolean = false;

    /** The German sentence of a failed append; see {@link appendFailed}. */
    appendErrorMessage: string = null;

    // --- Expand state -------------------------------------------------------------------------- //

    expandedRows: { [key: number]: boolean } = {};
    detailLoadingRows: { [key: number]: boolean } = {};
    contractDetails: { [key: number]: ProvisionContractTransactionDetailDto } = {};

    /** Rows the detail endpoint answered with 204 — a valid outcome, not a failure. */
    detailWithoutContract: { [key: number]: boolean } = {};

    /** Per-row failure text of the detail call. */
    detailErrors: { [key: number]: string } = {};

    // --- Dialog state -------------------------------------------------------------------------- //

    showManualBookingDialog: boolean = false;
    showTransferDialog: boolean = false;

    /** Is a cp job currently running? The statement is then an interim state. */
    jobRunning: boolean;

    protected readonly ProvisionAccountKind = ProvisionAccountKind;
    protected readonly ProvisionTransactionStatus = ProvisionTransactionStatus;

    private objectChangeSubscription: Subscription;
    private statusSubscription: Subscription;
    private jobWasRunning: boolean;

    /**
     * Monotonically growing ticket of the statement requests — the ONLY thing that decides which
     * response may write into this component.
     *
     * Every trigger (account switch, checkbox, sort, period, "load more", refresh) fires a new
     * request WITHOUT cancelling the previous one, and HTTP answers in no guaranteed order. Without
     * a ticket a slow "Rücklagenkonto" response landing after a fast "Provisionskonto" one paints
     * reserve lines under the heading "Provisionskonto — realer Saldo", on the very screen payouts
     * are decided on. Each request takes the next ticket; a response whose ticket is no longer the
     * current one is dropped whole — data, `busy` and error state alike.
     */
    private statementSequence: number = 0;

    /** Same ticket mechanism for the separately loaded balance row (U-03 notice). */
    private balanceSequence: number = 0;

    /** The agent the in-flight statement request was built for — see {@link finishStatementLoad}. */
    private requestedProvisionAgentId: number = null;

    /** The anchor the in-flight statement request was built for — see {@link finishStatementLoad}. */
    private requestedAnchorTransactionId: number = null;

    /** Pending debounce timer of a TYPED valuta date; picking or clearing a date flushes it. */
    private valutaFilterTimer: number = null;

    /**
     * The anchor actually IN EFFECT — adopted from the {@link anchorTransactionId} input and
     * dropped by an account switch, which starts a new journal the old anchor does not belong to.
     *
     * The input itself is never written. A component that clears its own `@Input()` destroys a
     * value only the parent can restore: Angular calls `ngOnChanges` on a CHANGED reference, so a
     * parent re-emitting the SAME anchor after the switch would produce no change at all and the
     * deep link would stay silently lost. The input keeps saying what the parent asked for; this
     * field says what the component is showing.
     */
    private activeAnchorTransactionId: number = null;

    constructor(
        protected coreService: MvsCoreService,
        protected mvsObjectService: MvsObjectService,
        protected springBatchIndicatorService: JbSpringBatchIndicatorService,
        protected agentTransactionService: CpAgentTransactionService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.activeAnchorTransactionId = this.anchorTransactionId ?? null;
        this.subscribeToObjectChanges();
        this.subscribeToJobCompletion();
        this.refreshComponent();
    }

    /**
     * A change of agent or anchor restarts the page.
     *
     * While the FIRST load is still in flight (`initialized` is only set by the response callback)
     * no second request is started here — but the change is not lost either: {@link loadStatement}
     * records which agent/anchor the in-flight request was built for, and
     * {@link finishStatementLoad} compares that against the CURRENT inputs and reloads when they
     * drifted apart. An early `return` alone would leave the component showing the previous agent
     * for good.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['provisionAgentId'] && !changes['anchorTransactionId']) {
            return;
        }

        // A newly bound anchor becomes the effective one — BEFORE the `initialized` gate below, so
        // that finishStatementLoad's drift check compares against the current binding even for a
        // change that arrives while the very first load is still in flight. Guarded on the anchor
        // having ACTUALLY changed: an unrelated agent change must not resurrect an anchor that
        // handleAccountKindChange dropped, and the parent's binding is unchanged either way.
        if (changes['anchorTransactionId']) {
            this.activeAnchorTransactionId = this.anchorTransactionId ?? null;
        }

        // A pending keystroke debounce belongs to the PREVIOUS agent's filter input. Left running
        // it fires ~500ms into the new agent's statement and calls handleFilterChange(), which
        // resets a page the user may already have grown and costs a second skeleton flash. The
        // reload below covers the new state; the timer has nothing left to contribute.
        this.clearValutaFilterTimer();

        if (!this.initialized) {
            // Either the very first binding (initComponent loads in a moment) or a change during
            // the first load — finishStatementLoad picks it up from the recorded request inputs.
            return;
        }
        this.refreshComponent();
    }

    ngOnDestroy(): void {
        this.objectChangeSubscription?.unsubscribe();
        this.statusSubscription?.unsubscribe();
        this.clearValutaFilterTimer();
    }

    /**
     * Reloads on every change to the module's account bookings — the two action dialogs and any
     * other part of the UI report through this channel.
     */
    private subscribeToObjectChanges(): void {
        this.objectChangeSubscription = this.mvsObjectService.objectChangeEvent.subscribe(change => {
            if (change.objectType === 'cp.ProvisionAgentTransaction') {
                this.refreshComponent();
            }
        });
    }

    /**
     * Subscription to the job indicator; reload on the running -&gt; not-running transition (04 §4).
     *
     * The indicator is neither built nor activated here — that is done by the shell CpDashboardPage
     * (`jobNamePattern: 'cp%'`). If the OBC runs outside that shell, `status$` simply never
     * delivers a transition; the component then behaves as before.
     */
    private subscribeToJobCompletion(): void {
        this.statusSubscription = this.springBatchIndicatorService.status$.subscribe(status => {
            const wasRunning: boolean = this.jobRunning;

            this.jobRunning = status.hasRunningJobs;

            if (status.hasRunningJobs) {
                this.jobWasRunning = true;
            } else if (this.jobWasRunning && wasRunning && !status.hasRunningJobs) {
                this.jobWasRunning = false;
                this.refreshComponent();
            }
        });
    }

    /**
     * Reloads the persisted balance row and RESTARTS the statement of the selected account at its
     * first page.
     *
     * Restarting — rather than re-fetching the pages that were appended — is the only defensible
     * answer to the two events that call this: a booking was written, or a cp job finished. Both
     * INSERT lines into the journal, and an inserted line shifts every keyset boundary behind it, so
     * the cursors of the accumulated pages describe a journal that no longer exists. Re-walking them
     * would stitch a statement out of two different versions of the ledger. The list therefore
     * starts again from the end the caseworker entered at, with the numbers that are now true.
     */
    refreshComponent(): void {

        if (!this.provisionAgentId) {
            // Invalidate whatever is still in flight — its response belongs to an agent that is no
            // longer bound and must not paint anything back into the cleared component.
            this.statementSequence++;
            this.balanceSequence++;
            this.requestedProvisionAgentId = null;
            this.requestedAnchorTransactionId = null;
            this.resetStatement();
            this.balanceDto = null;
            this.balanceLoadFailed = false;
            this.reserveSimulatedForecastNegative = false;
            this.busy = false;
            this.appending = false;
            this.initialized = true;
            return;
        }

        this.loadBalanceRow();
        this.restartStatement();
    }

    /**
     * Loads the FIRST page of the current account and filter, REPLACING everything accumulated.
     *
     * The single entry point for every restart — account switch, filter, sort, refresh, drift
     * catch-up. It starts at {@link activeAnchorTransactionId}, i.e. at the deep-linked booking when
     * one is bound and at the end of the journal otherwise; the paging cursor of the discarded list
     * plays no part, which is what makes this a restart and not a continuation.
     */
    private restartStatement(): void {
        this.loadStatement(this.activeAnchorTransactionId, false);
    }

    /**
     * Reads the persisted `cp.ProvisionAgentBalance` row of the agent — the source of the SIMULATED
     * reserve FORECAST, which must stay visible on the Provisionskonto too (D3). A missing row (the
     * balance job never ran) is NOT a shortfall and raises no alarm.
     *
     * The payout brake is not read here; it travels with the statement response.
     */
    private loadBalanceRow(): void {

        const sequence: number = ++this.balanceSequence;

        const filterCriteria = [
            FilterCriteria.create('provisionAgent', FilterCriteria.cOperatorEqual, this.provisionAgentId)
        ];

        const objectRequestList = ObjectRequestList.createBasic(false, filterCriteria, null);

        this.coreService.getCrudService('cp.ProvisionAgentBalance')
            .list(objectRequestList)
            .subscribe({
                next: (dtoList) => {
                    if (sequence !== this.balanceSequence) {
                        return;
                    }
                    const entries = dtoList?.entries as ProvisionAgentBalanceDto[];
                    this.balanceDto = entries && entries.length ? entries[0] : null;
                    this.balanceLoadFailed = false;
                    this.evaluateSimulatedForecast();
                },
                error: () => {
                    if (sequence !== this.balanceSequence) {
                        return;
                    }
                    // A MISSING row (the balance job never ran) is not an underfunding — but a
                    // FAILED read is not a missing row either. The distinction is carried into the
                    // template, which then says that a shortfall would not be visible here.
                    this.balanceDto = null;
                    this.balanceLoadFailed = true;
                    this.evaluateSimulatedForecast();
                }
            });
    }

    /**
     * Derives the SIMULATED reserve FORECAST from the loaded balance row. A null balance is not
     * negative.
     *
     * It deliberately derives NOTHING ELSE. The payout brake {@link reserveUnderfunded} is read from
     * the statement response, which takes it from `ProvisionPayoutService` — the authority that
     * actually stops payouts. The `reserveBalance < 0` that used to stand here was a copy of that
     * rule and is gone.
     *
     * The forecast is also not suppressed here when the brake is active: the brake arrives with a
     * DIFFERENT, independently timed request, so a suppression written into this method would depend
     * on which of the two answers lands first. The template holds the precedence instead — one
     * `@if`/`@else if` chain, evaluated on whatever both flags currently say.
     */
    private evaluateSimulatedForecast(): void {
        const reserveSimulated: number = this.balanceDto?.reserveSimulatedBalance;

        this.reserveSimulatedForecastNegative = reserveSimulated != null && reserveSimulated < 0;
    }

    /**
     * Loads one statement page for the selected account.
     *
     * A refusal (HTTP 400) carries a German sentence from `ProvisionCheckRefusedException`; it is
     * read out of `error.error.data` and shown inline. The global `HttpErrorInterceptor` already
     * toasts the same sentence — this component adds the inline copy so the empty table says WHY
     * it is empty, and it never substitutes a generic text.
     *
     * Requests are SEQUENCED, not cancelled: every call takes the next {@link statementSequence}
     * ticket and both callbacks drop their response when the ticket has moved on. That covers the
     * data, the error state AND the busy flags — the first answer to arrive must not clear the
     * skeleton of a later request, or the content would change again underneath the user.
     *
     * WITH APPENDING THE TICKET MATTERS MORE, NOT LESS. A dropped response must append nothing AND
     * must not advance {@link nextAnchorTransactionId}: both happen exclusively inside
     * {@link handleResponse}, which is only reached once the ticket and the `accountKind`
     * counter-check have both passed. A cursor moved on by a discarded answer would skip a whole
     * page of a money journal without any visible trace.
     *
     * @param requestAnchorTransactionId the anchor to ask for — the deep-link anchor on a restart,
     *                                   the forward CURSOR on an append
     * @param append                     true to APPEND the answer to the lines already on screen,
     *                                   false to REPLACE them
     */
    private loadStatement(requestAnchorTransactionId: number, append: boolean): void {

        const sequence: number = ++this.statementSequence;
        const requestedAccountKind: ProvisionAccountKind = this.accountKind;

        this.requestedProvisionAgentId = this.provisionAgentId;

        // The DEEP-LINK anchor the request was built under — deliberately not the anchor actually
        // sent, which on an append is the cursor. finishStatementLoad compares this against the
        // current binding to catch an input that changed mid-flight; comparing the cursor instead
        // would report drift after every single append and restart the list in an endless loop.
        this.requestedAnchorTransactionId = this.activeAnchorTransactionId;

        if (append) {
            this.appending = true;
        } else {
            this.busy = true;
            this.appending = false;
            this.loadFailed = false;
            this.errorMessage = null;
        }

        this.appendFailed = false;
        this.appendErrorMessage = null;

        const request: ProvisionAccountStatementRequestDto = {
            provisionAgentId: this.provisionAgentId,
            accountKind: this.accountKind,
            includeCalculated: this.includeCalculated,
            includeCancelled: this.includeCancelled,
            direction: this.direction,
            pageSize: this.pageSize ?? null,
            anchorTransactionId: requestAnchorTransactionId,
            valutaDateFrom: this.toIsoDate(this.filterValutaFrom),
            valutaDateTo: this.toIsoDate(this.filterValutaTo)
        };

        this.agentTransactionService.getAccountStatement(request).subscribe({
            next: (response: ProvisionAccountStatementResponseDto) => {

                if (sequence !== this.statementSequence) {
                    return;
                }

                // Safety net on top of the ticket: the response NAMES the account it covers
                // (ProvisionAccountStatementResponseDto.accountKind). Should it ever disagree with
                // the selection the request was built for, the lines are shown under the wrong
                // heading — so they are not shown at all. Compared with `!==` on purpose:
                // PROVISION_ACCOUNT is the ordinal 0 and therefore falsy.
                // An APPEND is not exempt: a mismatched answer is discarded WHOLE — nothing is
                // appended, the cursor stands still, and resetStatement clears the list rather than
                // leaving lines of an unverifiable provenance under a balance column. This is the
                // one place where an append does drop what was accumulated, and it is the right
                // trade: a server anomaly on the screen that decides payouts is answered by showing
                // nothing, not by showing something that may belong to the other account.
                if (response?.accountKind !== requestedAccountKind
                    || response.accountKind !== this.accountKind) {
                    this.resetStatement();
                    this.loadFailed = true;
                    this.errorMessage = 'Die Antwort des Servers gehört zu einem anderen Konto und '
                        + 'wurde verworfen. Bitte erneut laden.';
                    this.finishStatementLoad(append);
                    return;
                }

                this.handleResponse(response, append);
                this.finishStatementLoad(append);
            },
            error: (error) => {

                if (sequence !== this.statementSequence) {
                    return;
                }

                if (append) {
                    // The accumulated lines were read successfully and every running balance on them
                    // is an absolute figure, so they stay. Only the NEXT page is missing, the cursor
                    // is untouched, and the same button repeats the very same request.
                    this.appendFailed = true;
                    this.appendErrorMessage = this.readRefusalText(error)
                        ?? 'Weitere Buchungen konnten nicht geladen werden.';
                    this.finishStatementLoad(append);
                    return;
                }

                this.resetStatement();
                this.loadFailed = true;
                this.errorMessage = this.readRefusalText(error)
                    ?? 'Der Kontoauszug konnte nicht geladen werden.';
                this.finishStatementLoad(append);
            }
        });
    }

    /**
     * Closes the load of the CURRENT request and catches up on inputs that changed while it ran.
     *
     * `initialized` is what {@link ngOnChanges} needs before it may act, and it is only true from
     * here on. A change of agent or anchor inside that window would therefore be dropped; instead
     * the inputs the request was built for are compared with the current ones, and the newer agent
     * wins with one follow-up load.
     */
    private finishStatementLoad(append: boolean): void {

        if (append) {
            this.appending = false;
        } else {
            this.busy = false;
        }

        this.initialized = true;

        if (this.provisionAgentId !== this.requestedProvisionAgentId
            || this.activeAnchorTransactionId !== this.requestedAnchorTransactionId) {
            this.refreshComponent();
        }
    }

    /**
     * Adopts one statement page — either REPLACING the list or APPENDING behind it.
     *
     * On an append the expand state is deliberately KEPT: the rows it belongs to are still on
     * screen, and collapsing them because further lines arrived below would undo the caseworker's
     * own reading. Only a replacing load drops it, because those rows are gone.
     *
     * @param response the accepted page
     * @param append   true to append behind the accumulated lines, false to replace them
     */
    private handleResponse(response: ProvisionAccountStatementResponseDto, append: boolean): void {

        const page: ProvisionAgentTransactionWithBalanceDto[] = response.transactions ?? [];

        if (append) {
            this.transactions = this.transactions.concat(page);
        } else {
            this.transactions = page;
            this.resetExpandState();
        }

        // HEAD FIGURES — agent- and filter-scoped, repeated identically by every page, adopted from
        // the LAST accepted answer. Not frozen at the first page: between two clicks the balance job
        // may have run, and the freshest read of the same numbers is the truer one. Not per-page
        // either — none of these describes the chunk of lines just fetched.
        this.currentBalance = response.currentBalance;
        this.simulatedBalance = response.simulatedBalance;
        this.lastCalculatedDate = response.lastCalculatedDate;
        this.totalTransactionCount = response.totalTransactionCount;

        // What the balance COLUMN follows — from the backend, never from the requested filter. It
        // cannot change across an append (a filter change restarts the list), so adopting it again
        // is a no-op that keeps the column labelled by the answer that filled it.
        this.runningBalanceSimulated = response.runningBalanceSimulated;

        // THE PAYOUT BRAKE, read — not derived. `=== true` rather than a truthiness test, so an
        // absent field from an older backend reads as "no brake stated" instead of throwing the
        // banner on an undefined.
        this.reserveUnderfunded = response.reserveUnderfunded === true;

        this.adoptOpeningBalance(response, append, page.length);

        // THE CURSOR — advanced only here, i.e. only from an accepted answer. `?? null` keeps the
        // end-of-journal signal a strict null so `hasMore` can probe it with `!= null`.
        this.nextAnchorTransactionId = response.nextAnchorTransactionId ?? null;
    }

    /**
     * Adopts the Saldovortrag — the ONE figure of the response that does not simply belong to the
     * newest page.
     *
     * `openingBalance` is the balance before the JOURNAL-OLDEST line OF THAT PAGE. The row on screen
     * states the balance before the journal-oldest line of EVERYTHING loaded, so it must come from
     * whichever page holds that line:
     *
     * <ul>
     *   <li>REPLACING load — one page, and it holds the line. Adopt.</li>
     *   <li>APPEND, `'desc'` (newest first) — reading runs towards OLDER bookings, so every appended
     *       page reaches further back and the newest one now holds the journal-oldest line. Adopt.</li>
     *   <li>APPEND, `'asc'` (oldest first) — reading runs towards NEWER bookings, so the FIRST page
     *       still holds the journal-oldest line. Keep what is there; adopting would print the seed
     *       of the last chunk under a table reaching far further back, and the column above it would
     *       no longer continue from the stated figure.</li>
     *   <li>APPEND of an EMPTY page — an empty page has no line to compute a balance before and
     *       answers `null` ({@code ProvisionAgentAccountStatementService.emptyPage}). Adopting that
     *       would erase a known Saldovortrag into "—" for no reason.</li>
     * </ul>
     *
     * `?? null` on purpose: "not determined" must stay distinguishable from the number 0 all the way
     * to the "—" in the template.
     */
    private adoptOpeningBalance(response: ProvisionAccountStatementResponseDto,
                                append: boolean,
                                pageLineCount: number): void {

        if (!append) {
            this.openingBalance = response.openingBalance ?? null;
            return;
        }

        if (pageLineCount === 0 || this.direction === 'asc') {
            return;
        }

        this.openingBalance = response.openingBalance ?? null;
    }

    /**
     * Clears everything a page carries — used on error and when no agent is bound.
     *
     * The money figures go to `null`, NOT to `0`: the head block lives outside the busy/error
     * branches and keeps rendering, so a zeroed `currentBalance` would print a green "0,00 €" next
     * to the red failure message and read as "settled account, payout harmless". `null` renders as
     * "—" — a gap, which is what it is.
     */
    private resetStatement(): void {
        this.transactions = [];
        this.currentBalance = null;
        this.simulatedBalance = null;
        this.lastCalculatedDate = null;
        this.openingBalance = null;
        this.nextAnchorTransactionId = null;
        this.totalTransactionCount = null;

        // The brake travels WITH the statement, so a cleared statement knows nothing about it. It
        // goes back to false rather than staying on a value the last, discarded page delivered:
        // a red "Auszahlungen werden gebremst" left standing over a failure message would assert a
        // brake for an agent nobody has read.
        this.reserveUnderfunded = false;

        // A failed append reports beside a table that no longer exists once this runs.
        this.appendFailed = false;
        this.appendErrorMessage = null;

        this.resetExpandState();
    }

    private resetExpandState(): void {
        this.expandedRows = {};
        this.detailLoadingRows = {};
        this.contractDetails = {};
        this.detailWithoutContract = {};
        this.detailErrors = {};
    }

    /**
     * The German refusal sentence out of a `ProvisionCheckRefusedException` response.
     *
     * `CpControllerExceptionAdvice` answers HTTP 400 with `CoreApiResponseDto`, whose message sits
     * in `data`. Anything that is not a non-empty string yields null so the caller falls back to
     * its own wording.
     */
    private readRefusalText(error: any): string {
        const data = error?.error?.data;
        return typeof data === 'string' && data.trim().length ? data : null;
    }

    /**
     * `Date` -&gt; ISO `yyyy-MM-dd`, built from the LOCAL calendar fields. `toISOString()` would
     * convert to UTC first and could move a German date one day back.
     */
    private toIsoDate(date: Date): string {
        if (!date) {
            return null;
        }
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        return date.getFullYear() + '-' + month + '-' + day;
    }

    // --- User interaction ---------------------------------------------------------------------- //

    /**
     * Account switch — a new account means a new journal, so the page restarts at its own end.
     *
     * The anchor is dropped because it names a booking of the OTHER account; it is dropped on
     * {@link activeAnchorTransactionId} and NOT on the `@Input()` — see that field for why writing
     * the input would make a re-emitted anchor unrestorable.
     */
    handleAccountKindChange(): void {
        this.activeAnchorTransactionId = null;
        this.restartStatement();
    }

    /**
     * Any filter change RESTARTS the list — accumulated pages and the paging cursor alike.
     *
     * Both belong to the old filter: the cursor is a keyset position inside the old result set, and
     * the lines were selected and accumulated under the old scope. Continuing either one across a
     * filter change would append lines of the new filter behind lines of the old one, under a single
     * running-balance column.
     */
    handleFilterChange(): void {
        this.restartStatement();
    }

    /** Resets period and scope filters to the defaults of the statement. */
    handleFilterReset(): void {
        this.clearValutaFilterTimer();
        this.includeCalculated = true;
        this.includeCancelled = false;
        this.filterValutaFrom = null;
        this.filterValutaTo = null;
        this.handleFilterChange();
    }

    /**
     * A valuta bound was TYPED into the date field.
     *
     * `(onSelect)` fires only for a date picked in the overlay, so a hand-typed date used to change
     * the model and never reach the filter — the user saw their period in the field and an unchanged
     * table. The picker's own `ngModelChange` fires per parsed keystroke instead, which is why the
     * reload is scheduled rather than fired: while "01.02.2026" is being typed, "01.02.202" also
     * parses, and each intermediate date would otherwise cost a request.
     */
    handleValutaFromInput(value: Date): void {
        this.filterValutaFrom = value;
        this.scheduleValutaFilterReload();
    }

    /** See {@link handleValutaFromInput} — same for the upper bound. */
    handleValutaToInput(value: Date): void {
        this.filterValutaTo = value;
        this.scheduleValutaFilterReload();
    }

    /**
     * A date was PICKED in the overlay or CLEARED — an unambiguous, finished value, so the pending
     * keystroke debounce is dropped and the filter applies at once.
     *
     * {@link loadMore} follows the same rule from the other side: a click that arrives while the
     * debounce is still pending flushes it here instead of paging.
     */
    applyValutaFilterNow(): void {
        this.clearValutaFilterTimer();
        this.handleFilterChange();
    }

    private scheduleValutaFilterReload(): void {
        this.clearValutaFilterTimer();
        this.valutaFilterTimer = window.setTimeout(() => {
            this.valutaFilterTimer = null;
            this.handleFilterChange();
        }, 500);
    }

    private clearValutaFilterTimer(): void {
        if (this.valutaFilterTimer != null) {
            window.clearTimeout(this.valutaFilterTimer);
            this.valutaFilterTimer = null;
        }
    }

    /** Toggles the display order between oldest-first and newest-first. */
    toggleSortDirection(): void {
        this.direction = this.direction === 'asc' ? 'desc' : 'asc';
        this.handleFilterChange();
    }

    /**
     * Loads the NEXT page over the forward cursor and APPENDS it — the whole of "Weitere Buchungen
     * laden".
     *
     * {@link nextAnchorTransactionId} is sent back verbatim as `anchorTransactionId`. It is the
     * EXCLUSIVE anchor, i.e. it names the first line beyond the page on screen, and the backend
     * consumes `anchorTransactionId` INCLUSIVELY as the first line of the page it answers — so the
     * two pages abut exactly, with no repeated and no skipped booking. Nothing else changes: same
     * account, same filter, same direction, same page size.
     *
     * This replaces the earlier mechanism, which grew the REQUESTED PAGE SIZE by one step and
     * re-read the whole page. That version stopped dead at the module ceiling and left the screen at
     * 200 of some 10.000 bookings, with the valuta filter as the only way further back. There is no
     * ceiling on this path: the cursor walks the journal to its end, one page per click.
     *
     * Guarded on both in-flight flags. Two rapid clicks would otherwise fire the SAME cursor twice;
     * the ticket would drop the first answer, so nothing would duplicate — but the request is
     * pointless, and the guard keeps the button honest about what it is doing.
     *
     * Guarded FIRST on a pending valuta debounce. A typed bound changes the filter immediately but
     * schedules the reload 500ms out, so within that window the cursor on screen still belongs to
     * the OLD filter while the request would be built from the NEW one — the page would be read at
     * an anchor the filter no longer places anywhere, appending unrelated lines or reporting the
     * journal as exhausted. The click therefore flushes the debounce via
     * {@link applyValutaFilterNow} and lets the restart read page one under the new filter, which is
     * the same rule the picker's `(onSelect)`/`(onClear)` already follow.
     */
    loadMore(): void {

        if (this.valutaFilterTimer != null) {
            this.applyValutaFilterNow();
            return;
        }

        if (this.busy || this.appending || !this.hasMore) {
            return;
        }

        this.loadStatement(this.nextAnchorTransactionId, true);
    }

    /**
     * True while further booking lines exist in the DISPLAY DIRECTION.
     *
     * Read from the EXCLUSIVE {@link nextAnchorTransactionId}: `null` is the module's reliable
     * end-of-journal signal, and it names the continuation point rather than only asserting that one
     * exists. `!= null` and not a truthiness test — an id is a number and ids are never 0 here, but
     * the module's ordinal enums have taught this file to compare explicitly.
     *
     * There is no second condition any more. The module ceiling used to cap the range and had to be
     * asked whether the button could still do anything; over the cursor the ceiling sizes ONE page
     * and the end of the journal is the only stopping point.
     */
    get hasMore(): boolean {
        return this.nextAnchorTransactionId != null;
    }

    /**
     * Marks the row a deep link pointed at, so it stays recognisable inside the page. Follows the
     * EFFECTIVE anchor: after an account switch the deep-linked booking is no longer part of this
     * journal and must not be highlighted, even though the input still names it.
     */
    isAnchorTransaction(transaction: ProvisionAgentTransactionWithBalanceDto): boolean {
        return this.activeAnchorTransactionId != null
            && transaction.id === this.activeAnchorTransactionId;
    }

    /**
     * Expands or collapses one booking row, loading the contract side lazily.
     *
     * The call only happens for `hasContractDetail === true`; that flag is resolved for the whole
     * page in ONE batched backend query, so an expand that would come back empty never fires a
     * request. A 204 (manual booking, payout — no contract counterpart) arrives as `null` and is a
     * VALID outcome: it renders a calm note, never an error.
     */
    toggleRowExpansion(transaction: ProvisionAgentTransactionWithBalanceDto): void {

        if (this.expandedRows[transaction.id]) {
            this.expandedRows[transaction.id] = false;
            return;
        }

        if (transaction.hasContractDetail !== true) {
            return;
        }

        if (this.contractDetails[transaction.id] || this.detailWithoutContract[transaction.id]) {
            this.expandedRows[transaction.id] = true;
            return;
        }

        this.detailLoadingRows[transaction.id] = true;
        this.detailErrors[transaction.id] = null;

        this.agentTransactionService.getContractTransactionDetail(transaction.id).subscribe({
            next: (detail: ProvisionContractTransactionDetailDto) => {
                if (detail) {
                    this.contractDetails[transaction.id] = detail;
                } else {
                    this.detailWithoutContract[transaction.id] = true;
                }
                this.expandedRows[transaction.id] = true;
                this.detailLoadingRows[transaction.id] = false;
            },
            error: (error) => {
                this.detailErrors[transaction.id] = this.readRefusalText(error)
                    ?? 'Die Vertragsdaten zu dieser Buchung konnten nicht geladen werden.';
                this.expandedRows[transaction.id] = true;
                this.detailLoadingRows[transaction.id] = false;
            }
        });
    }

    /** Is the expand row of this booking open? */
    isRowExpanded(transactionId: number): boolean {
        return this.expandedRows[transactionId] === true;
    }

    openManualBookingDialog(): void {
        if (!this.canManualBooking) {
            return;
        }
        this.showManualBookingDialog = true;
    }

    openTransferDialog(): void {
        if (!this.canReserveTransfer) {
            return;
        }
        this.showTransferDialog = true;
    }

    /** Why "Manuelle Buchung" is locked, in German — a disabled button always names its reason. */
    get manualBookingTooltip(): string {
        return this.canManualBooking
            ? 'Manuelle Buchung auf einem Konto dieses Abrechnungskontos erfassen.'
            : CpFeatureRight.deniedMessage(CpFeatureRight.MANUAL_BOOKING);
    }

    /** Why "Umbuchung" is locked, in German — a disabled button always names its reason. */
    get reserveTransferTooltip(): string {
        return this.canReserveTransfer
            ? 'Zwischen Rücklagen- und Provisionskonto dieses Abrechnungskontos umbuchen.'
            : CpFeatureRight.deniedMessage(CpFeatureRight.RESERVE_TRANSFER);
    }

    /** The manual booking dialog saw a 403 — demote the right so the entry button goes quiet too. */
    handleManualBookingForbidden(): void {
        this.canManualBooking = false;
    }

    /** The transfer dialog saw a 403 — same demotion for `cp_reserveTransfer`. */
    handleReserveTransferForbidden(): void {
        this.canReserveTransfer = false;
    }

    /** A booking was written — reload head figures and lines. */
    handleBookingDone(): void {
        this.refreshComponent();
    }

    // --- Display helpers ------------------------------------------------------------------------ //

    /** German name of the selected account, for the head and the empty state. */
    get accountKindLabel(): string {
        return getProvisionAccountKindLabel(this.accountKind);
    }

    /**
     * Header of the running-balance column. Driven by
     * {@link ProvisionAccountStatementResponseDto#runningBalanceSimulated} — what the backend
     * actually accumulated — and NOT by the requested filter, which may have been clamped.
     */
    get balanceColumnLabel(): string {
        return this.runningBalanceSimulated ? 'Saldo (simuliert)' : 'Saldo (real)';
    }

    /**
     * Colour of ONE money figure, decided by the SIGN OF THE DELIVERED NUMBER and by nothing else
     * (FD-1 / B1) — `bookingKind` never reaches this method.
     *
     * Exactly `0` is NEUTRAL, not green: a settled account is not an account in credit, and painting
     * the most common boundary value in the "money is there" colour is a statement nobody made. An
     * unknown figure (`null`, e.g. after a failed load) is neutral too and is printed as "—" by the
     * template.
     */
    amountToneClass(value: number): string {
        if (value == null || value === 0) {
            return 'text-color-secondary';
        }
        return value < 0 ? 'text-red-600' : 'text-green-600';
    }

    /**
     * Caption of the Saldovortrag row.
     *
     * The backend seeds the running balance with the balance BEFORE the JOURNAL-OLDEST line of the
     * page, then reverses the page for a descending display. The row therefore describes the OLDEST
     * shown booking in both directions — which is the first row for `'asc'` and the last one for
     * `'desc'` — so the caption names that booking instead of a position in the table.
     *
     * A constant, therefore a `readonly` FIELD and not a getter: a getter returning a literal is
     * re-evaluated on every change-detection pass for a string that can never differ.
     */
    readonly openingBalanceLabel: string =
        'Saldovortrag — Saldo vor der ältesten angezeigten Buchung';

    /**
     * German caption of the booking direction. THE ONLY use of `bookingKind` in this component:
     * a column caption. It never becomes a sign, a colour or an operand (FD-1).
     *
     * ── THE FOUR LABEL HELPERS BELOW ARE FORWARDERS, NOT TABLES
     *
     * The German names of the four enums live next to the enums, in
     * `enum/provision-*-label.ts`, the way `provision-run-status-label.ts` established for the
     * module. What used to stand here were inline `switch` maps with `default: return ''` — and
     * every one of those enums explicitly provides for new constants being APPENDED. An appended
     * constant rendered its column as an EMPTY CELL: no compile error, no warning, just a blank
     * where a provenance or a status should be. The companions fall through to a visible fallback
     * instead. These methods exist only because an Angular template cannot call a bare imported
     * function.
     */
    bookingKindLabel(bookingKind: ProvisionBookingKind): string {
        return getProvisionBookingKindLabel(bookingKind);
    }

    /** German label of the booking status — see `enum/provision-transaction-status-label.ts`. */
    statusLabel(status: ProvisionTransactionStatus): string {
        return getProvisionTransactionStatusLabel(status);
    }

    /** Tag severity of the booking status — a readability cue, never a sign statement. */
    statusSeverity(status: ProvisionTransactionStatus): CpTransactionStatusSeverity {
        return getProvisionTransactionStatusSeverity(status);
    }

    /** German label of the booking provenance — see `enum/provision-transaction-event-label.ts`. */
    eventLabel(event: ProvisionTransactionEvent): string {
        return getProvisionTransactionEventLabel(event);
    }

}
